@layer token {
  :root {
    --n-floating-content-border-width: var(--n-border-width-025);
    --n-floating-content-border-style: solid;

    --n-floating-content-shadow: var(--n-shadow-300);

    --n-floating-content-font-size: var(--n-font-size-350);
    --n-floating-content-font-weight: var(--n-font-weight-400);
    --n-floating-content-lh: var(--n-line-height-500);
    --n-floating-content-letter-spacing: var(--n-font-letter-spacing-000);

    /* VARIANTS ------------------------------------------------------------- */

    /* DEFAULT VARIANT ------------------------------------------------------ */
    --n-floating-content-default-padding-block: var(--n-scale-300);
    --n-floating-content-default-padding-inline: var(--n-scale-300);
    --n-floating-content-default-width-min: auto;
    --n-floating-content-default-width-max: none;
    --n-floating-content-default-border-radius: var(--n-border-radius-300);
    --n-floating-content-default-border-color: var(
      --n-color-border-neutral-base
    );
    --n-floating-content-default-bg-color-base: var(--n-color-content-inverse);
    --n-floating-content-default-bg-color: var(
      --n-floating-content-default-bg-color-base
    );
    --n-floating-content-default-backdrop-filter: blur(10px);
    --n-floating-content-default-font-color: var(
      --n-color-content-neutral-base
    );

    /* TOOLTIP VARIANT ------------------------------------------------------ */
    --n-floating-content-tooltip-padding-block: var(--n-scale-100);
    --n-floating-content-tooltip-padding-inline: var(--n-scale-200);
    --n-floating-content-tooltip-width-min: auto;
    --n-floating-content-tooltip-width-max: 240px;
    --n-floating-content-tooltip-border-radius: var(--n-border-radius-150);
    --n-floating-content-tooltip-border-color: var(
      --n-floating-content-tooltip-bg-color
    );
    --n-floating-content-tooltip-bg-color: var(
      --n-color-surface-neutral-contrast
    );
    --n-floating-content-tooltip-font-color: var(--n-color-content-inverse);

    /* ARROW ---------------------------------------------------------------- */
    --n-floating-content-arrow-base-length: 19px;
    --n-floating-content-arrow-base-to-tip-length: 10px;
    --n-floating-content-arrow-offset: calc(
      var(--n-floating-content-arrow-base-to-tip-length) - 1px
    );
  }
}

@layer component {
  .floatingContent {
    position: relative;
    box-shadow: var(--n-floating-content-shadow);
    font-size: var(--n-floating-content-font-size);
    font-weight: var(--n-floating-content-font-weight);
    line-height: var(--n-floating-content-lh);
    letter-spacing: var(--n-floating-content-letter-spacing);
  }

  /* VARIANTS --------------------------------------------------------------- */

  .defaultVariant {
    min-width: var(--n-floating-content-default-width-min);
    max-width: var(--n-floating-content-default-width-max);
    padding-block: var(--n-floating-content-default-padding-block);
    padding-inline: var(--n-floating-content-default-padding-inline);
    border-radius: var(--n-floating-content-default-border-radius);
    border-width: var(--n-floating-content-border-width);
    border-color: var(--n-floating-content-default-border-color);
    border-style: var(--n-floating-content-border-style);
    backdrop-filter: var(--n-floating-content-default-backdrop-filter);
    background-color: var(--n-floating-content-default-bg-color);
    color: var(--n-floating-content-default-font-color);
  }

  .tooltipVariant {
    min-width: var(--n-floating-content-tooltip-width-min);
    max-width: var(--n-floating-content-tooltip-width-max);
    padding-block: var(--n-floating-content-tooltip-padding-block);
    padding-inline: var(--n-floating-content-tooltip-padding-inline);
    border-radius: var(--n-floating-content-tooltip-border-radius);
    border-width: var(--n-floating-content-border-width);
    border-color: var(--n-floating-content-tooltip-border-color);
    border-style: var(--n-floating-content-border-style);
    background-color: var(--n-floating-content-tooltip-bg-color);
    color: var(--n-floating-content-tooltip-font-color);
  }

  /* ARROW ------------------------------------------------------------------ */

  .arrow {
    position: absolute;
  }

  .arrow::before,
  .arrow::after {
    content: '';
    position: absolute;
    inset: 0;
    clip-path: var(--arrow-clip);
  }

  .defaultVariant .arrow::before {
    transform: translate(var(--arrow-border-x), var(--arrow-border-y));
    background-color: var(--n-floating-content-default-border-color);
  }

  .defaultVariant .arrow::after {
    background-color: var(--n-floating-content-default-bg-color-base);
  }

  .tooltipVariant .arrow::after {
    background-color: var(--n-floating-content-tooltip-bg-color);
  }

  :global([data-placement='top']) .arrow {
    bottom: calc(var(--n-floating-content-arrow-offset) * -1);
    left: 50%;
    transform: translateX(-50%);
    width: var(--n-floating-content-arrow-base-length);
    height: var(--n-floating-content-arrow-base-to-tip-length);
    --arrow-clip: polygon(0 0, 100% 0, 50% 100%);
    --arrow-border-x: 0;
    --arrow-border-y: var(--n-floating-content-border-width);
  }

  :global([data-placement='right']) .arrow {
    left: calc(var(--n-floating-content-arrow-offset) * -1);
    top: 50%;
    transform: translateY(-50%);
    width: var(--n-floating-content-arrow-base-to-tip-length);
    height: var(--n-floating-content-arrow-base-length);
    --arrow-clip: polygon(100% 0, 100% 100%, 0 50%);
    --arrow-border-x: calc(var(--n-floating-content-border-width) * -1);
    --arrow-border-y: 0;
  }

  :global([data-placement='bottom']) .arrow {
    top: calc(var(--n-floating-content-arrow-offset) * -1);
    left: 50%;
    transform: translateX(-50%);
    width: var(--n-floating-content-arrow-base-length);
    height: var(--n-floating-content-arrow-base-to-tip-length);
    --arrow-clip: polygon(50% 0, 100% 100%, 0 100%);
    --arrow-border-x: 0;
    --arrow-border-y: calc(var(--n-floating-content-border-width) * -1);
  }

  :global([data-placement='left']) .arrow {
    right: calc(var(--n-floating-content-arrow-offset) * -1);
    top: 50%;
    transform: translateY(-50%);
    width: var(--n-floating-content-arrow-base-to-tip-length);
    height: var(--n-floating-content-arrow-base-length);
    --arrow-clip: polygon(0 0, 100% 50%, 0 100%);
    --arrow-border-x: var(--n-floating-content-border-width);
    --arrow-border-y: 0;
  }
}
